import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@cian/ui-kit';

import {
  isValuesValid,
  prepareInitialValues,
  prepareSubmitValues,
  prepareTrackingParams,
  useFormShowAnalytics,
  usePhoneFillAnalytics,
  useRequirementsFillAnalytics,
} from './utils';
import { trackCallbackClickAction } from '../../actions/analytics';
import { createConsultingRequestAction } from '../../actions/consultingRequest';
import { openRequestForm } from '../../actions/pageState';
import {
  RequestFormModalContent,
  RequestFormModalFooter,
  RequestFormModalWindow,
  SuccessModalWindow,
} from '../../components/RequestFormModal';
import { selectLocation } from '../../selectors/location';
import { selectIsSubmitting, selectPageState } from '../../selectors/pageState';
import { selectUserPhone } from '../../selectors/user';
import { getPopularRegions } from '../../services/popularRegions';
import { searchRegionSuggests } from '../../services/regionSuggests';
import { EPageState } from '../../types/pageState';
import { TThunkDispatch } from '../../types/redux/thunk';
import { IRegion, IRequestFormValues } from '../../types/requestForm';
import { useApplicationContext } from '../../utils/applicationContext';
import { RequestFormModalPersonalAgreementContainer } from '../RequestFormModalPersonalAgreement';

const INITIAL_VALUES: IRequestFormValues = {
  phone: '',
  region: null,
  requirements: '',
};

export interface IRequestFormModalWindowContainerProps {
  open: boolean;
  onClose: () => void;
}

export const RequestFormModalWindowContainer: React.FC<IRequestFormModalWindowContainerProps> = ({ open, onClose }) => {
  const [values, setValues] = React.useState<IRequestFormValues>(INITIAL_VALUES);
  const [isValid, setIsValid] = React.useState(false);
  const popularRegionsRef = React.useRef<IRegion[] | null>(null);

  const isSubmitting = useSelector(selectIsSubmitting);
  const location = useSelector(selectLocation);
  const userPhone = useSelector(selectUserPhone);
  const pageState = useSelector(selectPageState);
  const dispatch: TThunkDispatch = useDispatch();
  const context = useApplicationContext();

  useFormShowAnalytics(open);

  const analyticsPhoneChangeHandler = usePhoneFillAnalytics();
  const analyticsRequirementsChangeHandler = useRequirementsFillAnalytics();

  const handleRequirementsChange = React.useCallback(() => {
    analyticsRequirementsChangeHandler(values.phone);
  }, [analyticsRequirementsChangeHandler, values.phone]);

  const updateValues = React.useCallback((nextValues: Partial<IRequestFormValues>) => {
    setValues(prev => ({ ...prev, ...nextValues }));
  }, []);

  const searchRegion = React.useCallback(
    async (text: string): Promise<IRegion[]> => {
      if (text.length >= 3) {
        return searchRegionSuggests(context, { text });
      }

      if (!popularRegionsRef.current) {
        popularRegionsRef.current = await getPopularRegions(context);
      }

      return popularRegionsRef.current;
    },
    [context],
  );

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      if (isSubmitting) {
        return;
      }

      try {
        const preparedValues = prepareSubmitValues(values);
        await dispatch(createConsultingRequestAction(preparedValues));
      } catch {
        // ignore exception
      }

      dispatch(trackCallbackClickAction(prepareTrackingParams(values)));
    },
    [dispatch, values, isSubmitting],
  );

  const handleSuccessModalClosing = React.useCallback(() => {
    dispatch(openRequestForm());
    setValues(INITIAL_VALUES);
    updateValues(prepareInitialValues({ location, userPhone }));
    onClose();
  }, [dispatch, onClose]);

  React.useEffect(() => {
    updateValues(prepareInitialValues({ location, userPhone }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setIsValid(isValuesValid(values));
  }, [values]);

  if (pageState === EPageState.RequestSent) {
    return <SuccessModalWindow onClose={handleSuccessModalClosing} open={open} />;
  }

  return (
    <RequestFormModalWindow
      content={
        <RequestFormModalContent
          onChange={updateValues}
          onPhoneChange={analyticsPhoneChangeHandler}
          onRegionSearch={searchRegion}
          onRequirementsChange={handleRequirementsChange}
          submitting={isSubmitting}
          values={values}
        />
      }
      footer={
        <RequestFormModalFooter
          personalAgreement={<RequestFormModalPersonalAgreementContainer />}
          submitButton={
            <Button disabled={!isValid} onClick={handleSubmit} loading={isSubmitting} fullWidth>
              Заказать звонок
            </Button>
          }
        />
      }
      onClose={onClose}
      open={open}
    />
  );
};
